import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PlanResults from "@/components/energy/PlanResults";
import { ToolShell, NumInput, SelectInput, ErrorBox } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

const KIND_META = {
  business: {
    eyebrow: "Solar for My Business",
    title: "Build your business energy plan.",
    lead: "Select your business type for a pre-filled equipment profile, adjust anything, then build your plan.",
    cta: "Build My Business Energy Plan",
    propertyType: "shop",
  },
  agriculture: {
    eyebrow: "Solar for Agriculture",
    title: "Power for pumps, poultry and processing.",
    lead: "Irrigation, boreholes, cold storage, crop processing and farm lighting — sized for Sierra Leone farms.",
    cta: "Request Agricultural Energy Assessment",
    propertyType: "farm",
  },
  schools: {
    eyebrow: "School Energy Assessment",
    title: "Reliable power for learning.",
    lead: "Classrooms, fans, computers and evening study — build a preliminary school energy plan.",
    cta: "Request BNM School Energy Project",
    propertyType: "school",
  },
  clinics: {
    eyebrow: "Clinic & Health Facility Energy Assessment",
    title: "Critical-load-first power for health facilities.",
    lead: "Preliminary planning tool — it does not replace professional electrical or medical facility engineering assessment. All critical loads are prioritised for outages.",
    cta: "Request Clinic Energy Assessment",
    propertyType: "clinic",
  },
};

const SCHOOL_FIELDS = [
  { key: "classrooms", label: "Number of classrooms", def: 4 },
  { key: "students", label: "Number of students (informational)", def: 100 },
  { key: "computers", label: "Computers", def: 5 },
  { key: "projectors", label: "Projectors", def: 1 },
  { key: "tvs", label: "TVs", def: 1 },
  { key: "printers", label: "Printers", def: 1 },
  { key: "water_pumps", label: "Water pumps", def: 0 },
];

const CLINIC_FIELDS = [
  { key: "lights", label: "Lighting points", def: 8 },
  { key: "vaccine_fridges", label: "Vaccine / medical refrigerators", def: 1 },
  { key: "refrigerators", label: "General refrigerators", def: 0 },
  { key: "computers", label: "Computers", def: 1 },
  { key: "medical_watts", label: "Other medical equipment (total W)", def: 300 },
  { key: "fans", label: "Fans", def: 4 },
  { key: "water_pumps", label: "Water pumps", def: 1 },
  { key: "comms", label: "Communications (router etc.)", def: 1 },
];

const AGRI_FIELDS = [
  { key: "pump_watts", label: "Pump size (W each)", def: 750 },
  { key: "pumps", label: "Number of pumps", def: 1 },
  { key: "pump_hours", label: "Pumping hours / day", def: 3 },
  { key: "poultry_lights", label: "Poultry / farm lights", def: 6 },
  { key: "cold_storage", label: "Cold storage units (freezers)", def: 0 },
  { key: "machinery_watts", label: "Processing machinery (W)", def: 1000 },
  { key: "machinery_hours", label: "Machinery hours / day", def: 3 },
];

function buildItems(kind, config, presetKey, fields) {
  if (kind === "business") {
    const preset = config.business_presets[presetKey];
    return (preset?.items || []).map(([name, watts, hours, surge, qty], idx) => ({
      key: `preset_${idx}`, name, watts, hours, surge, qty, days_per_week: 7, essential: true, period: "both",
    }));
  }
  if (kind === "schools") {
    const f = fields;
    return [
      { name: "Classroom LED lights", watts: 10, hours: 8, surge: 1, qty: (f.classrooms || 0) * 2, period: "both", essential: true },
      { name: "Classroom fans", watts: 75, hours: 6, surge: 1.5, qty: (f.classrooms || 0) * 2, period: "day", essential: false },
      { name: "Computers", watts: 200, hours: 4, surge: 1, qty: f.computers || 0, period: "day", essential: false },
      { name: "Projectors", watts: 250, hours: 2, surge: 1, qty: f.projectors || 0, period: "day", essential: false },
      { name: "TVs", watts: 100, hours: 2, surge: 1, qty: f.tvs || 0, period: "day", essential: false },
      { name: "Printers", watts: 100, hours: 1, surge: 1.5, qty: f.printers || 0, period: "day", essential: false },
      { name: "Water pump", watts: 750, hours: 2, surge: 3, qty: f.water_pumps || 0, period: "both", essential: true },
      { name: "Evening study lighting", watts: 10, hours: 3, surge: 1, qty: (f.classrooms || 0), period: "night", essential: true },
    ].filter((i) => i.qty > 0).map((i, idx) => ({ ...i, key: `school_${idx}`, days_per_week: 5 }));
  }
  if (kind === "clinics") {
    const f = fields;
    return [
      { name: "Lighting", watts: 10, hours: 12, surge: 1, qty: f.lights || 0, period: "both" },
      { name: "Vaccine / medical refrigerator", watts: 150, hours: 24, surge: 3, qty: f.vaccine_fridges || 0, period: "both" },
      { name: "General refrigerator", watts: 150, hours: 12, surge: 3, qty: f.refrigerators || 0, period: "both" },
      { name: "Computers", watts: 200, hours: 6, surge: 1, qty: f.computers || 0, period: "day" },
      { name: "Medical equipment", watts: f.medical_watts || 0, hours: 4, surge: 1.5, qty: 1, period: "both" },
      { name: "Fans", watts: 75, hours: 10, surge: 1.5, qty: f.fans || 0, period: "both" },
      { name: "Water pump", watts: 750, hours: 2, surge: 3, qty: f.water_pumps || 0, period: "both" },
      { name: "Communications equipment", watts: 30, hours: 24, surge: 1, qty: f.comms || 0, period: "both" },
    ].filter((i) => i.qty > 0 && i.watts > 0).map((i, idx) => ({ ...i, key: `clinic_${idx}`, days_per_week: 7, essential: true }));
  }
  // agriculture
  const f = fields;
  return [
    { name: "Water / irrigation pump", watts: f.pump_watts || 0, hours: f.pump_hours || 0, surge: 3, qty: f.pumps || 0, period: "day", essential: true },
    { name: "Poultry / farm lighting", watts: 10, hours: 12, surge: 1, qty: f.poultry_lights || 0, period: "night", essential: true },
    { name: "Cold storage (freezer)", watts: 400, hours: 14, surge: 3, qty: f.cold_storage || 0, period: "both", essential: true },
    { name: "Processing machinery", watts: f.machinery_watts || 0, hours: f.machinery_hours || 0, surge: 3, qty: (f.machinery_watts || 0) > 0 ? 1 : 0, period: "day", essential: false },
  ].filter((i) => i.qty > 0 && i.watts > 0).map((i, idx) => ({ ...i, key: `agri_${idx}`, days_per_week: 7 }));
}

const FIELD_SETS = { schools: SCHOOL_FIELDS, clinics: CLINIC_FIELDS, agriculture: AGRI_FIELDS };

export default function EnergyPresets() {
  const { kind } = useParams();
  const meta = KIND_META[kind] || KIND_META.business;
  const [config, setConfig] = useState(null);
  const [presetKey, setPresetKey] = useState("barbing_salon");
  const [location, setLocation] = useState("");
  const [fields, setFields] = useState(() => Object.fromEntries((FIELD_SETS[kind] || []).map((f) => [f.key, f.def])));
  const [items, setItems] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/energy/config").then((r) => setConfig(r.data)).catch(() => setError("Could not load. Please refresh."));
  }, []);

  const fieldDefs = FIELD_SETS[kind] || [];

  const build = async () => {
    const built = buildItems(kind, config, presetKey, fields);
    if (!built.length) { setError("Please configure at least one piece of equipment."); return; }
    setBusy(true);
    setError("");
    try {
      const profile = { property_type: meta.propertyType, location };
      const { data } = await api.post("/energy/plan", { profile, appliances: built, goal: "whole_property" });
      setItems(data.items);
      setResult({ plan: data.plan, items: data.items, profile });
      setTimeout(() => document.getElementById("preset-results")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={`${meta.eyebrow} — BNM Energy Intelligence`} description={meta.lead} />
      <ToolShell testid={`presets-${kind}`} eyebrow={meta.eyebrow} title={meta.title} lead={meta.lead}>
        {error && <div className="mb-6"><ErrorBox msg={error} /></div>}
        {!config ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8 space-y-6">
            <TextInputRow label="Location / community" testid="preset-location" value={location} onChange={setLocation} />
            {kind === "business" && (
              <SelectInput label="Business type" testid="preset-business-type" value={presetKey} onChange={setPresetKey}
                options={Object.entries(config.business_presets).map(([value, p]) => ({ value, label: p.label }))} />
            )}
            {fieldDefs.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {fieldDefs.map((f) => (
                  <NumInput key={f.key} label={f.label} min={0} max={f.key === "students" ? 5000 : 500} step={f.key.includes("watts") ? "any" : 1}
                    testid={`preset-field-${f.key}`} value={fields[f.key]}
                    onChange={(v) => setFields((s) => ({ ...s, [f.key]: v }))} />
                ))}
              </div>
            )}
            {kind === "business" && (
              <p className="text-xs text-slate-500">
                Pre-filled equipment: {(config.business_presets[presetKey]?.items || []).map((i) => `${i[4]}× ${i[0]}`).join(", ") || "none — describe your equipment in the message after building"}.
              </p>
            )}
            <button onClick={build} disabled={busy} data-testid="preset-build"
              className="inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
              {busy ? "Building…" : meta.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
        {result && (
          <div id="preset-results" className="mt-10 scroll-mt-28">
            <PlanResults plan={result.plan} items={result.items} profile={result.profile} />
          </div>
        )}
      </ToolShell>
    </>
  );
}

function TextInputRow({ label, value, onChange, testid }) {
  return (
    <label className="block max-w-md">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-12 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600" />
    </label>
  );
}
